import React, {useState, useEffect, useCallback} from 'react';
import {Modal, Input, Button, Space, message, Spin, Card, Tag} from 'antd';
import {
  RobotOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  PlusOutlined
} from '@ant-design/icons';
import {ChartConfig} from '../../types';
import {
  AIChartService,
  ChartGenerationProgress
} from '../../services/AIChartService';
import {VChart} from '@visactor/react-vchart';
import '../../style/AIGenerateModal.css';

interface AIGenerateModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (charts: ChartConfig[]) => void;
  currentSheetId?: string;
  api: any;
  fieldList: any[];
  props: any;
}

type GenerationStatus = 'idle' | 'generating' | 'done' | 'error';
type ChartGenerationTask = {
  status: 'pending' | 'generating' | 'success' | 'error';
  chartConfig: ChartConfig | null;
  error?: string;
};

export const AIGenerateModal: React.FC<AIGenerateModalProps> = ({
  visible,
  onClose,
  onConfirm,
  currentSheetId,
  api,
  fieldList,
  props
}) => {
  const [aiChartService] = useState(() => new AIChartService(props));
  const [generationStatus, setGenerationStatus] =
    useState<GenerationStatus>('idle');
  const [customPrompt, setCustomPrompt] = useState('');
  const [tasks, setTasks] = useState<ChartGenerationTask[]>([]);
  const [selectedChartIds, setSelectedChartIds] = useState<Set<number>>(
    new Set()
  );

  useEffect(() => {
    if (!visible) {
      setTimeout(() => {
        setGenerationStatus('idle');
        setTasks([]);
        setCustomPrompt('');
        setSelectedChartIds(new Set());
      }, 300);
    }
  }, [visible]);

  const handleGenerate = async (useCustomPrompt = false, maxCharts = 4) => {
    if (!currentSheetId || !api) {
      message.error('无法生成仪表盘，缺少工作表ID或API');
      return;
    }

    setGenerationStatus('generating');
    setSelectedChartIds(new Set());
    setTasks(Array(maxCharts).fill({status: 'pending', chartConfig: null, error: ''}));

    try {
      const postFields = fieldList.map(field => field.field);
      const sheetData = await api.getRecords(currentSheetId, postFields);
      if (!sheetData || sheetData.length === 0) {
        message.error('当前工作表没有数据，无法生成图表。');
        setGenerationStatus('error');
        return;
      }

      const promptToUse =
        useCustomPrompt && customPrompt.trim()
          ? customPrompt.trim()
          : undefined;

      const onProgress = (progress: ChartGenerationProgress) => {
        setTasks(prev => {
          const newTasks = [...prev];
          newTasks[progress.index] = {
            status: progress.status,
            chartConfig: progress.chartConfig || null,
            error: progress.error
          };
          return newTasks;
        });

        if (progress.status === 'success' && progress.chartConfig) {
          setSelectedChartIds(prev =>
            new Set(prev).add(progress.chartConfig!.id)
          );
        }
      };
        console.log("fieldData奇怪", sheetData);

      await aiChartService.generateChartsWithProgress(
        {
          data: sheetData,
          fields: [],
          sheetId: currentSheetId,
          customPrompt: promptToUse,
          maxCharts: 4
        },
        onProgress
      );

      setGenerationStatus('done');
    } catch (error: any) {
      console.error('AI生成仪表盘失败:', error);
      message.error(`AI生成仪表盘失败: ${error.message}`);
      setGenerationStatus('error');
    }
  };

  const handleToggleSelectChart = (chartId: number) => {
    setSelectedChartIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chartId)) {
        newSet.delete(chartId);
      } else {
        newSet.add(chartId);
      }
      return newSet;
    });
  };

  const handleConfirm = () => {
    const selectedCharts = tasks
      .filter(
        task => task.chartConfig && selectedChartIds.has(task.chartConfig.id)
      )
      .map(task => task.chartConfig!);
    onConfirm(selectedCharts);
    onClose();
  };

  const renderIdleState = () => (
    <div className="ai-modal-content idle-state">
      <p className="prompt-description">
        请输入您的分析需求，或直接使用快捷指令，AI将为您智能生成数据图表。
      </p>
      <Input.TextArea
        className="prompt-input"
        placeholder="例如：帮我分析各产品的销售额和利润趋势"
        value={customPrompt}
        onChange={e => setCustomPrompt(e.target.value)}
        rows={3}
        maxLength={500}
        showCount
      />
      <div
        className="quick-action-button"
        onClick={() => handleGenerate(false, 4)}
      >
        <span>为当前工作表一键生成智能分析图表</span>
        <SendOutlined />
      </div>
      <div className="action-footer">
        <Button
          type="primary"
          icon={<RobotOutlined />}
          onClick={() => handleGenerate(true, 1)}
          disabled={!customPrompt.trim()}
        >
          开始分析
        </Button>
      </div>
    </div>
  );

 const renderGeneratingState = () => {
  const isSingleTask = tasks.length === 1;
  
  return (
    <div className="ai-modal-content generating-state">
      <div className="loading-indicator">
        <Spin />
        <p className="ant-spin-text">AI 正在分析数据，请稍候...</p>
        <p className="loading-subtext">正在为您生成可视化图表建议</p>
      </div>
      <div className={`task-grid ${isSingleTask ? 'single-task' : ''}`}>
        {tasks.map((task, index) => (
          <Card
            key={index}
            size="small"
            className={`task-card ${task.status !== 'pending' ? 'active' : ''}`}
          >
            <div className="task-card-content">
              {task.status === 'pending' && (
                <div>
                  <Spin size="small" />
                </div>
              )}
              {task.status === 'generating' && <Spin tip="生成中..." />}
              {task.status === 'success' && <CheckCircleOutlined />}
              {task.status === 'error' && <CloseCircleOutlined />}
            </div>
            <p className="task-card-title">图表 {index + 1}</p>
          </Card>
        ))}
      </div>
    </div>
  );
};

  const renderDoneState = () => (
    <div className="ai-modal-content done-state">
      <p className="done-description">
        AI 已生成以下图表，请选择您需要采纳的图表：
      </p>
      <div className="chart-grid">
        {tasks.map((task, index) => {
          if (task.status === 'success' && task.chartConfig) {
            const isSelected = selectedChartIds.has(task.chartConfig.id);
            return (
              <Card
                key={index}
                hoverable
                title={task.chartConfig.title}
                className={`chart-preview-card ${isSelected ? 'selected' : ''}`}
                onClick={() => handleToggleSelectChart(task.chartConfig!.id)}
                extra={
                  <Button
                    type={isSelected ? 'primary' : 'default'}
                    shape="circle"
                    icon={<PlusOutlined />}
                    size="small"
                  />
                }
              >
                <div className="chart-container">
                  <VChart
                    spec={task.chartConfig.chartSpec}
                    options={{mode: 'desktop-browser'}}
                  />
                </div>
              </Card>
            );
          }
          if (task.status === 'error') {
            return (
              <Card
                key={index}
                title={`图表 ${index + 1} 生成失败`}
                className="chart-preview-card"
              >
                <div className="chart-error-state">
                  <CloseCircleOutlined />
                  <p>{task.error || '未知错误'}</p>
                </div>
              </Card>
            );
          }
          return null;
        })}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (generationStatus) {
      case 'generating':
        return renderGeneratingState();
      case 'done':
      case 'error':
        return renderDoneState();
      case 'idle':
      default:
        return renderIdleState();
    }
  };

  const getModalWidth = () => {
    if (generationStatus === 'done' || generationStatus === 'error') {
      return '80vw';
    }
    return 600;
  };

  return (
    <Modal
      centered
      title={
        <div className="ai-modal-title">
          <RobotOutlined />
          <span>AI 智能分析助手</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={getModalWidth()}
      style={{maxWidth: '1200px'}}
      destroyOnClose
      className="ai-generate-modal-root"
      footer={
        <div className="ai-modal-footer">
          {generationStatus !== 'idle' ? (
            <Button
              icon={<SyncOutlined />}
              onClick={() => handleGenerate(customPrompt.trim().length > 0)}
              disabled={generationStatus === 'generating'}
            >
              重新生成
            </Button>
          ) : (
            <div />
          )}
          <Space>
            <Button onClick={onClose}>取消</Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleConfirm}
              disabled={
                generationStatus !== 'done' || selectedChartIds.size === 0
              }
            >
              采纳 {selectedChartIds.size} 个图表
            </Button>
          </Space>
        </div>
      }
    >
      {renderContent()}
    </Modal>
  );
};
